import styled from '@emotion/styled';
import { useEffect, useMemo, useRef } from 'react';
import { cloneDeep } from 'lodash-es';
import { useTranslation } from 'react-i18next';

import { tokens, Text, Tooltip } from '@hotjar/hotsauce';
import { convertFromNewUiToQueryFormat } from '@hotjar/global-filters';
import type { GlobalFilter } from '@hotjar/global-filters';
import { viewElement } from '@hotjar/tracking';

import { HEATMAP_DATA_TYPES } from '../../constants-types/HeatmapDataTypes';
import { useGetAverageWindowHeight } from '../../hooks/useGetAverageWindowHeight';
import {
  addDeviceTypeToGlobalFilters,
  addTargetingRuleToGlobalFilters,
} from '../../utils/generateNavigationLinkFromHeatmaps';
import { useHeatmapsRawData } from '../../hooks/useHeatmapsRawData';
import { processScrollMarkers } from '../../utils/processScrollMarkers';
import type { TrackableSite } from '../../constants-types/ExternalDependencyTypes';
import { EVENT_UI_POSITIONS } from '../../constants-types/TrackEventUIPositions';
import { EVENT_NAMES } from '../../constants-types/TrackEventNames';
import { getTrackGroupDataAndTriggeredOnState } from '../../utils/trackingUtils';

const CONTAINER_CLASS_NAME = 'hj-hm-ruler-container';
const MARKER_OVERLAP_THRESHOLD_PX = 35;

const Container = styled.div<{ isVisible: boolean }>`
  position: relative;
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  opacity: ${({ isVisible }) => (isVisible ? '1' : '0')};
  height: 100%;
  transition: opacity 0.5s linear;
  border-right: 1px solid ${tokens.color.ui_border};
`;

const Marker = styled.div<{ percentFromTop: number; hasBgColor?: boolean }>`
  position: absolute;
  width: 100%;
  top: 0;
  height: ${({ percentFromTop }) => `calc(${percentFromTop}%)`};
  border-bottom: 1px solid ${tokens.color.ui_border};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding-bottom: ${tokens.spacer.x1};
  pointer-events: none;

  ${({ hasBgColor }) => hasBgColor === true && `background-color: ${tokens.color.bg_info};`}
`;

const MarkerText = styled(Text)`
  writing-mode: vertical-rl;
  transform: rotate(-180deg);
  padding: ${tokens.spacer.x0pt5} 0;
  border-radius: ${tokens.radius.m};
  pointer-events: initial;

  &:hover {
    background-color: ${tokens.color.bg_hover};
  }
`;

function fireTrackingEvent(name: string, currentSite?: TrackableSite) {
  if (currentSite !== undefined) {
    viewElement({
      name,
      uiPosition: EVENT_UI_POSITIONS.MAGIC_RULER,
      ...getTrackGroupDataAndTriggeredOnState(currentSite),
    });
  }
}

function fireScrollMarkerTrackingEvent(value: string, currentSite?: TrackableSite) {
  let name = EVENT_NAMES.MAGIC_RULER_75_MARKER;

  if (value.includes('50')) name = EVENT_NAMES.MAGIC_RULER_50_MARKER;
  if (value.includes('25')) name = EVENT_NAMES.MAGIC_RULER_25_MARKER;

  fireTrackingEvent(name, currentSite);
}

interface MagicRulerProps {
  imageHeight: number;
  siteId: number;
  selectedFiltersInUiFormat: GlobalFilter;
  selectedTargetingValue: TargetingValue;
  searchPattern: string;
  selectedDeviceType: HeatmapDeviceTypes;
  isDisabled: boolean;
  // Adding this a separate prop to avoid conditional rendering,
  // as it is only needed for tracking events.
  currentSite?: TrackableSite;
}

export const MagicRuler = ({
  imageHeight,
  siteId,
  selectedFiltersInUiFormat,
  selectedTargetingValue,
  searchPattern,
  selectedDeviceType,
  isDisabled,
  currentSite,
}: MagicRulerProps): JSX.Element | null => {
  const { t } = useTranslation('heatmaps');

  const reachMarkerHigherThanWindowHeightRef = useRef<boolean>(false);
  const markerOverlapRef = useRef<boolean>(false);

  const globalFilters: GlobalFilter = useMemo(() => {
    const filters = cloneDeep(selectedFiltersInUiFormat);
    addDeviceTypeToGlobalFilters(selectedDeviceType, filters);
    addTargetingRuleToGlobalFilters(selectedTargetingValue, searchPattern, filters);
    return filters;
  }, [selectedFiltersInUiFormat, selectedDeviceType, selectedTargetingValue, searchPattern]);

  const averageWindowHeight = useGetAverageWindowHeight(globalFilters, siteId, !isDisabled);
  const isAverageWindowHeightAvailable = Boolean(averageWindowHeight);

  const { rawHeatData: scrollDataResult } = useHeatmapsRawData({
    siteId,
    deviceType: selectedDeviceType,
    heatmapType: HEATMAP_DATA_TYPES.SCROLL,
    matchOperation: selectedTargetingValue,
    url: searchPattern,
    jsonQueryString: JSON.stringify(convertFromNewUiToQueryFormat(globalFilters)),
    enabled: isAverageWindowHeightAvailable,
  });

  const scrollData =
    scrollDataResult.data?.dataPoints !== undefined
      ? (scrollDataResult.data?.dataPoints as number[])
      : undefined;
  const isScrollDataAvailable = scrollData !== undefined && scrollData.length > 0;

  const isVisible =
    isAverageWindowHeightAvailable && scrollDataResult.isSuccess !== false && isScrollDataAvailable;

  const scrollReachMarkers = isScrollDataAvailable
    ? processScrollMarkers(imageHeight, scrollData)
    : [];

  // Calculations for checking if a reach marker is higher than the window height.
  const averageWindowPercentFromTop =
    averageWindowHeight !== null ? (averageWindowHeight / imageHeight) * 100 : null;
  const hasReachMarkerHigherThanWindowHeight =
    averageWindowPercentFromTop !== null &&
    scrollReachMarkers.some(({ top }) => top < averageWindowPercentFromTop);

  // Fire a tracking event if a reach marker is higher than the window height.
  useEffect(() => {
    if (!hasReachMarkerHigherThanWindowHeight) {
      reachMarkerHigherThanWindowHeightRef.current = false;
      return;
    }

    if (
      isVisible &&
      hasReachMarkerHigherThanWindowHeight &&
      !reachMarkerHigherThanWindowHeightRef.current
    ) {
      reachMarkerHigherThanWindowHeightRef.current = true;
      fireTrackingEvent(EVENT_NAMES.MAGIC_RULER_MARKER_HIGHER_THAN_FOLD, currentSite);
    }
  }, [hasReachMarkerHigherThanWindowHeight, currentSite, isVisible]);

  // Calculations for marker overlap checks.
  const markerOverlapPercent = (MARKER_OVERLAP_THRESHOLD_PX / imageHeight) * 100;
  let hasMarkerOverlap = false;
  scrollReachMarkers.forEach(({ top }, index) => {
    if (index === scrollReachMarkers.length - 1) return;

    if (Math.abs(top - scrollReachMarkers[index + 1].top) < markerOverlapPercent)
      hasMarkerOverlap = true;
  });

  // Fire a tracking event if a marker overlap is detected.
  useEffect(() => {
    if (!hasMarkerOverlap) {
      markerOverlapRef.current = false;
      return;
    }

    if (isVisible && hasMarkerOverlap && !markerOverlapRef.current) {
      markerOverlapRef.current = true;
      fireTrackingEvent(EVENT_NAMES.MAGIC_RULER_MARKERS_OVERLAP, currentSite);
    }
  }, [hasMarkerOverlap, currentSite, isVisible]);

  if (isDisabled) return null;
  if (hasReachMarkerHigherThanWindowHeight) return null;

  return (
    <Container
      isVisible={isVisible}
      className={isVisible ? CONTAINER_CLASS_NAME : ''}
      data-testid={CONTAINER_CLASS_NAME}
    >
      {scrollReachMarkers.map(({ top, value }) => {
        return (
          <Marker key={value} percentFromTop={top}>
            {/* removing the % as it is already part of the translation text */}
            <Tooltip text={`${value.replace('%', '')}${t('scroll-markers.reach')}`}>
              <MarkerText onMouseEnter={() => fireScrollMarkerTrackingEvent(value, currentSite)}>
                {value}
              </MarkerText>
            </Tooltip>
          </Marker>
        );
      })}

      {averageWindowHeight !== null && averageWindowPercentFromTop !== null && (
        <Marker percentFromTop={averageWindowPercentFromTop} hasBgColor>
          <Tooltip text={t('scroll-markers.average_fold_desc')}>
            <MarkerText
              onMouseEnter={() =>
                fireTrackingEvent(EVENT_NAMES.MAGIC_RULER_AVERAGE_FOLD, currentSite)
              }
            >
              {`${t('scroll-markers.average-fold')}: ${averageWindowHeight.toFixed(0)}${t(
                'general-terms.unit_pixel',
              )}`}
            </MarkerText>
          </Tooltip>
        </Marker>
      )}
    </Container>
  );
};
